import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import AppNav from "../components/AppNav";
import { useDarkMode } from "../hooks/useDarkMode";
import usePulseLocale from "./i18n";
import { tokens } from "./theme";
import NewsletterPrompt from "./NewsletterPrompt";
import "./pulse.css";

export const LangToggle = ({ dm }) => {
  const { lang, setLang } = usePulseLocale();
  const T = tokens(dm);
  const btn = (code, testid) => (
    <button key={code} onClick={() => setLang(code)} data-testid={testid}
      aria-pressed={lang === code}
      className="text-[11px] font-bold uppercase tracking-wider px-2 py-1"
      style={{
        background: lang === code ? T.brand : "transparent",
        color: lang === code ? "#fff" : T.muted,
        border: `1px solid ${lang === code ? T.brand : T.border}`,
        borderRadius: 2,
      }}>
      {code}
    </button>
  );
  return (
    <div className="flex items-center gap-1" role="group" aria-label="Language">
      {btn("en", "pulse-lang-en")}
      {btn("it", "pulse-lang-it")}
    </div>
  );
};

/** Shell for all authenticated Pulse screens: AppNav + Pulse sub-nav +
 *  locale toggle + one-time newsletter prompt + attribution footer. */
export default function PulseLayout({ active = "world", children }) {
  const [dm] = useDarkMode();
  const { t, withLang } = usePulseLocale();
  const T = tokens(dm);
  // centre the active tab inside the horizontally scrollable nav — horizontal only, once per page.
  // (scrollIntoView in a callback ref re-ran on every render and yanked the page to the top, iter159 bug)
  const navRef = useRef(null);
  useEffect(() => {
    const nav = navRef.current;
    const el = nav && nav.querySelector(`[data-testid="pulse-subnav-${active}"]`);
    if (el && nav.scrollWidth > nav.clientWidth) {
      const left = el.getBoundingClientRect().left - nav.getBoundingClientRect().left + nav.scrollLeft;
      nav.scrollLeft = left - (nav.clientWidth - el.offsetWidth) / 2;
    }
  }, [active]);
  const items = [
    { key: "world", to: "/pulse", label: t("nav_world") },
    { key: "italy", to: "/pulse/italy", label: t("nav_italy") },
    { key: "ask", to: "/pulse/ask", label: t("nav_ask") },
    { key: "compare", to: "/pulse/compare", label: t("nav_compare") },
    { key: "rankings", to: "/pulse/rankings", label: t("nav_rankings") },
    { key: "cities", to: "/pulse/cities", label: t("nav_cities") },
  ];
  return (
    <div className="pulse-root min-h-screen flex flex-col" style={{ background: T.bg, color: T.text }}>
      <AppNav active="pulse" darkMode={dm} />
      <div style={{ borderBottom: `1px solid ${T.border}`, background: T.surface }}>
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-3">
          <nav ref={navRef} className="flex items-center gap-1 overflow-x-auto" aria-label="Pulse sections">
            {items.map((it) => (
              <Link key={it.key} to={withLang(it.to)} data-testid={`pulse-subnav-${it.key}`}
                aria-current={active === it.key ? "page" : undefined}
                className="text-xs font-bold uppercase tracking-wider px-3 py-3 whitespace-nowrap"
                style={{
                  color: active === it.key ? T.accent : T.muted,
                  borderBottom: `2px solid ${active === it.key ? T.brand : "transparent"}`,
                }}>
                {it.label}
              </Link>
            ))}
          </nav>
          <LangToggle dm={dm} />
        </div>
      </div>
      <main className="max-w-screen-xl mx-auto px-4 sm:px-6 py-6 w-full flex-1">
        {children}
      </main>
      <footer className="max-w-screen-xl mx-auto px-4 sm:px-6 py-6 w-full">
        <p className="text-[11px]" style={{ color: T.muted }} data-testid="pulse-footer-attribution">
          {t("footer_attribution")}
        </p>
      </footer>
      <NewsletterPrompt dm={dm} />
    </div>
  );
}
